import React, { useState, useEffect, useMemo } from 'react';
import { Save, Store, FileText, Bell, Database, Download, AlertTriangle, X, ImagePlus, Users as UsersIcon, Shield, GalleryHorizontal } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import api from '../api/axios';
import { UiTabs } from '../components/ui/UiTabs';
import { applyTheme } from '../utils/theme';
import useAuthStore from '../store/authStore';
import VoiceInput from '../components/VoiceInput';
import Users from './Users';
import ACLManagement from './ACLManagement';
import MediaManagement from './MediaManagement';
import { formatApiError } from '../utils/apiErrors';

const DATE_FORMAT_OPTIONS = [
  { value: 'DD/MM/YYYY', label: 'DD/MM/YYYY (e.g. 28/04/2026)' },
  { value: 'MM/DD/YYYY', label: 'MM/DD/YYYY (e.g. 04/28/2026)' },
  { value: 'YYYY/MM/DD', label: 'YYYY/MM/DD (e.g. 2026/04/28)' },
  { value: 'DAY, DD-MMM-YYYY', label: 'Day, DD-MMM-YYYY (e.g. Mon, 6-Apr-2026)' },
];

const Settings = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [settings, setSettings] = useState({
    shop_name: '',
    shop_address: '',
    shop_phone: '',
    shop_email: '',
    shop_tagline: '',
    theme_primary: '#F97316',
    tax_rate: 0,
    currency_symbol: 'Rs.',
    receipt_footer: '',
    low_stock_threshold: 10,
    expiry_warning_days: 7,
    business_day_start_hour: 6,
    late_threshold_minutes: 15,
    half_day_threshold_minutes: 60,
    early_departure_threshold_minutes: 30,
    auto_generate_performance_records: true,
    bonus_star_window_months: 9,
    cash_count_interval_minutes: 0,
    date_format: 'DD/MM/YYYY'
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [activeTab, setActiveTab] = useState('shop');
  const [backingUp, setBackingUp] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [resetConfirmText, setResetConfirmText] = useState('');

  const { hasPermission } = useAuthStore();
  const canViewSettings = hasPermission('settings', 'view');
  const canViewUsers = hasPermission('users', 'view');
  const canViewRBAC = hasPermission('users', 'edit');
  const canViewMedia = hasPermission('menu', 'view');
  const tabs = useMemo(() => ([
    { id: 'alerts', label: 'Options', icon: Bell, show: canViewSettings },
    { id: 'rbac', label: 'RBAC', icon: Shield, show: canViewRBAC },
    { id: 'users', label: 'Users', icon: UsersIcon, show: canViewUsers },
    { id: 'media', label: 'Media', icon: GalleryHorizontal, show: canViewMedia },
    { id: 'shop', label: 'Shop', icon: Store, show: canViewSettings },
    { id: 'receipt', label: 'Receipt', icon: FileText, show: canViewSettings },
    { id: 'system', label: 'Data', icon: Database, show: canViewSettings },
  ].filter(tab => tab.show)), [canViewMedia, canViewRBAC, canViewSettings, canViewUsers]);
  const tabIds = tabs.map(tab => tab.id);
  const requestedTab = new URLSearchParams(location.search).get('tab');
  const resolvedTab = tabIds.includes(requestedTab) ? requestedTab : (tabIds[0] || 'shop');
  const isSettingsFormTab = ['shop', 'receipt', 'alerts', 'system'].includes(activeTab);

  useEffect(() => {
    if (!canViewSettings) {
      setLoading(false);
      return;
    }
    fetchSettings();
  }, [canViewSettings]);

  useEffect(() => {
    setActiveTab(resolvedTab);
  }, [resolvedTab]);

  useEffect(() => {
    if (!tabIds.length) return;
    if (requestedTab === resolvedTab) return;
    const params = new URLSearchParams(location.search);
    params.set('tab', resolvedTab);
    navigate({ pathname: location.pathname, search: `?${params.toString()}` }, { replace: true });
  }, [location.pathname, location.search, navigate, requestedTab, resolvedTab, tabIds.length]);

  const fetchSettings = async () => {
    try {
      const res = await api.get('/settings/');
      if (res.data) {
        setSettings(res.data);
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const { shop_logo, ...settingsToPost } = settings;
      await api.post('/settings/', settingsToPost);
      if (logoFile) {
        const fd = new FormData();
        fd.append('model_type', 'settings');
        fd.append('image', logoFile);
        fd.append('field_name', 'shop_logo');
        await api.post('/upload-image/', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        setLogoFile(null);
      }
      toast.success('Settings saved successfully');
      fetchSettings();
    } catch (error) {
      toast.error(formatApiError(error, 'Failed to save settings'));
    } finally {
      setSaving(false);
    }
  };

  const handleLogoSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setLogoFile(file);
      setLogoPreview(URL.createObjectURL(file));
    }
  };

  const handleBackup = async () => {
    setBackingUp(true);
    try {
      const res = await api.get('/settings/backup/', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `pos_backup_${new Date().toISOString().split('T')[0]}.json`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Backup downloaded successfully');
    } catch (error) {
      toast.error(formatApiError(error, 'Failed to download backup'));
    } finally {
      setBackingUp(false);
    }
  };

  const handleReset = async () => {
    if (resetConfirmText !== 'RESET') {
      toast.error('Please type RESET to confirm');
      return;
    }
    setResetting(true);
    try {
      await api.post('/settings/reset/', { confirm: 'RESET' });
      toast.success('All transactional data has been reset');
      setShowResetConfirm(false);
      setResetConfirmText('');
    } catch (error) {
      toast.error(formatApiError(error, 'Failed to reset data'));
    } finally {
      setResetting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-800">Settings</h1>
          <p className="text-sm sm:text-base text-gray-500">Configure your POS system</p>
        </div>
        {isSettingsFormTab && hasPermission('settings', 'edit') && (
          <button onClick={handleSave} disabled={saving} className="bg-primary-600 hover:bg-primary-700 text-white px-4 sm:px-6 py-2 rounded-lg flex items-center gap-2 disabled:opacity-50 text-sm sm:text-base">
            {saving ? (
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
            ) : (
              <Save size={18} />
            )}
            Save Changes
          </button>
        )}
      </div>

      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        {/* Tabs */}
        <div className="border-b overflow-x-auto p-3 sm:p-4">
          <UiTabs
            tabs={tabs}
            active={activeTab}
            onChange={(id) => {
              const params = new URLSearchParams(location.search);
              params.set('tab', id);
              navigate({ pathname: location.pathname, search: `?${params.toString()}` });
            }}
          />
        </div>

        {/* Tab Content */}
        <div className="p-4 sm:p-6">
          {activeTab === 'shop' && (
            <div className="space-y-6 max-w-2xl">
              {/* Shop Logo */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Shop Logo</label>
                <div className="flex items-center gap-4">
                  {(logoPreview || settings.shop_logo) && (
                    <img
                      src={logoPreview || settings.shop_logo}
                      alt="Shop Logo"
                      className="w-20 h-20 rounded-xl object-contain border border-gray-200 bg-gray-50 p-1"
                    />
                  )}
                  <label className="flex items-center gap-2 px-4 py-2 border-2 border-dashed border-gray-300 rounded-xl cursor-pointer hover:border-primary-400 hover:bg-primary-50 transition text-sm text-gray-600">
                    <ImagePlus size={18} className="text-primary-500" />
                    {logoFile ? logoFile.name : 'Upload Logo'}
                    <input type="file" accept="image/*" onChange={handleLogoSelect} className="hidden" />
                  </label>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Shop Name *</label>
                <VoiceInput
                  type="text"
                  value={settings.shop_name}
                  onChange={(e) => setSettings({ ...settings, shop_name: e.target.value })}
                  placeholder="Enter shop name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Shop Tagline</label>
                <p className="text-xs text-gray-500 mb-2">Displayed on the dashboard header (e.g. "Juices . Icecream . Shakes")</p>
                <VoiceInput
                  type="text"
                  value={settings.shop_tagline || ''}
                  onChange={(e) => setSettings({ ...settings, shop_tagline: e.target.value })}
                  placeholder="Enter shop tagline"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Brand Color</label>
                <p className="text-xs text-gray-500 mb-2">The whole app palette (buttons, active tabs, highlights) is derived from this one color. Changes preview instantly; press Save to keep them.</p>
                <div className="flex items-center gap-3">
                  <input
                    type="color"
                    value={settings.theme_primary || '#F97316'}
                    onChange={(e) => { setSettings({ ...settings, theme_primary: e.target.value }); applyTheme(e.target.value); }}
                    className="h-10 w-14 rounded-lg border border-gray-200 cursor-pointer bg-white p-1"
                    aria-label="Brand color"
                  />
                  <input
                    type="text"
                    value={settings.theme_primary || '#F97316'}
                    onChange={(e) => { const v = e.target.value; setSettings({ ...settings, theme_primary: v }); applyTheme(v); }}
                    placeholder="#F97316"
                    className="w-32 px-3 py-2 border border-gray-200 rounded-lg text-sm font-mono focus:ring-2 focus:ring-primary-100 focus:border-primary-500"
                  />
                  <div className="flex items-center gap-1.5">
                    {['#F97316', '#0EA5E9', '#10B981', '#8B5CF6', '#E11D48', '#0D9488', '#4F46E5'].map((c) => (
                      <button
                        key={c}
                        type="button"
                        onClick={() => { setSettings({ ...settings, theme_primary: c }); applyTheme(c); }}
                        className="w-6 h-6 rounded-full border border-gray-200 hover:scale-110 transition"
                        style={{ backgroundColor: c }}
                        aria-label={`Use ${c}`}
                      />
                    ))}
                  </div>
                </div>
                <div className="mt-3 flex items-center gap-2">
                  <span className="px-3 py-1.5 rounded-lg bg-primary-600 text-white text-sm font-medium">Primary</span>
                  <span className="px-3 py-1.5 rounded-lg bg-primary-100 text-primary-700 text-sm font-medium">Soft</span>
                  <span className="px-3 py-1.5 rounded-lg border border-primary-300 text-primary-700 text-sm font-medium">Outline</span>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Shop Address *</label>
                <VoiceInput
                  isTextarea
                  value={settings.shop_address}
                  onChange={(e) => setSettings({ ...settings, shop_address: e.target.value })}
                  rows={3}
                  placeholder="Enter shop address"
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number</label>
                  <VoiceInput
                    type="tel"
                    value={settings.shop_phone}
                    onChange={(e) => setSettings({ ...settings, shop_phone: e.target.value })}
                    placeholder="Enter phone number"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                  <VoiceInput
                    type="email"
                    value={settings.shop_email}
                    onChange={(e) => setSettings({ ...settings, shop_email: e.target.value })}
                    placeholder="Enter email"
                  />
                </div>
              </div>
            </div>
          )}

          {activeTab === 'receipt' && (
            <div className="space-y-6 max-w-2xl">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Currency Symbol</label>
                  <VoiceInput
                    type="text"
                    value={settings.currency_symbol}
                    onChange={(e) => setSettings({ ...settings, currency_symbol: e.target.value })}
                    placeholder="Rs."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Tax Rate (%)</label>
                  <VoiceInput
                    type="number"
                    step="0.01"
                    value={settings.tax_rate}
                    onChange={(e) => setSettings({ ...settings, tax_rate: e.target.value })}
                    placeholder="0"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Receipt Footer Message</label>
                <VoiceInput
                  isTextarea
                  value={settings.receipt_footer}
                  onChange={(e) => setSettings({ ...settings, receipt_footer: e.target.value })}
                  rows={3}
                  placeholder="Thank you for visiting!"
                />
              </div>

              {/* Receipt Preview */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Receipt Preview</label>
                <div className="border-2 border-dashed border-gray-200 rounded-lg p-6 bg-gray-50">
                  <div className="max-w-xs mx-auto bg-white p-4 shadow-sm rounded">
                    <div className="text-center border-b pb-3 mb-3">
                      <h3 className="font-bold text-lg">{settings.shop_name || 'Shop Name'}</h3>
                      <p className="text-xs text-gray-500">{settings.shop_address || 'Shop Address'}</p>
                      <p className="text-xs text-gray-500">{settings.shop_phone || 'Phone'}</p>
                    </div>
                    <div className="text-xs space-y-1 border-b pb-3 mb-3">
                      <div className="flex justify-between">
                        <span>Sample Item x2</span>
                        <span>{settings.currency_symbol} 300.00</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Another Item x1</span>
                        <span>{settings.currency_symbol} 150.00</span>
                      </div>
                    </div>
                    <div className="text-xs space-y-1 mb-3">
                      <div className="flex justify-between">
                        <span>Subtotal</span>
                        <span>{settings.currency_symbol} 450.00</span>
                      </div>
                      {parseFloat(settings.tax_rate) > 0 && (
                        <div className="flex justify-between">
                          <span>Tax ({settings.tax_rate}%)</span>
                          <span>{settings.currency_symbol} {(450 * parseFloat(settings.tax_rate) / 100).toFixed(2)}</span>
                        </div>
                      )}
                      <div className="flex justify-between font-bold pt-1 border-t">
                        <span>Total</span>
                        <span>{settings.currency_symbol} {(450 * (1 + parseFloat(settings.tax_rate || 0) / 100)).toFixed(2)}</span>
                      </div>
                    </div>
                    <div className="text-center text-xs text-gray-500 pt-3 border-t">
                      {settings.receipt_footer || 'Thank you!'}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'alerts' && (
            <div className="space-y-6 max-w-2xl">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Global Date Format</label>
                <p className="text-xs text-gray-500 mb-2">Used across reports, receipts, filters, summaries, calendars, and printable views.</p>
                <select
                  value={settings.date_format || 'DD/MM/YYYY'}
                  onChange={(e) => { setSettings({ ...settings, date_format: e.target.value }); localStorage.setItem('pos_date_format', e.target.value); }}
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500"
                >
                  {DATE_FORMAT_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Business Day Start Hour</label>
                <p className="text-xs text-gray-500 mb-2">
                  In food business, a "day" typically starts at 06:00 and ends at 05:59 the next day. Set the hour (00-23) when your business day begins.
                </p>
                <select
                  value={settings.business_day_start_hour ?? 6}
                  onChange={(e) => setSettings({ ...settings, business_day_start_hour: parseInt(e.target.value) })}
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500"
                >
                  {Array.from({ length: 24 }, (_, i) => (
                    <option key={i} value={i}>
                      {`${String(i).padStart(2, '0')}:00`}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Till Cash-Count Interval (minutes)</label>
                <p className="text-xs text-gray-500 mb-2">Force the POS operator to record the total cash in the till when this many minutes have passed since the last count. On the next sale punch, the POS is blocked until the cash is entered. <strong>0 = off.</strong></p>
                <input
                  type="number" min="0" step="5"
                  value={settings.cash_count_interval_minutes ?? 0}
                  onChange={(e) => setSettings({ ...settings, cash_count_interval_minutes: Math.max(parseInt(e.target.value || '0', 10), 0) })}
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Low Stock Threshold</label>
                <p className="text-xs text-gray-500 mb-2">Alert when inventory falls below this quantity</p>
                <input
                  type="number"
                  value={settings.low_stock_threshold}
                  onChange={(e) => setSettings({ ...settings, low_stock_threshold: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Expiry Warning Days</label>
                <p className="text-xs text-gray-500 mb-2">Alert when items expire within this many days</p>
                <input
                  type="number"
                  value={settings.expiry_warning_days}
                  onChange={(e) => setSettings({ ...settings, expiry_warning_days: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500"
                />
              </div>

              {/* Staff Attendance Thresholds */}
              <div className="border-t pt-6 mt-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Staff Attendance Thresholds</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Late Threshold (minutes)</label>
                    <p className="text-xs text-gray-500 mb-2">Minutes after shift start to mark as late arrival</p>
                    <input
                      type="number"
                      value={settings.late_threshold_minutes ?? 15}
                      onChange={(e) => setSettings({ ...settings, late_threshold_minutes: parseInt(e.target.value) })}
                      className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Half-Day Threshold (minutes)</label>
                    <p className="text-xs text-gray-500 mb-2">Minutes after shift start to mark as half-day</p>
                    <input
                      type="number"
                      value={settings.half_day_threshold_minutes ?? 60}
                      onChange={(e) => setSettings({ ...settings, half_day_threshold_minutes: parseInt(e.target.value) })}
                      className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Early Departure Threshold (minutes)</label>
                    <p className="text-xs text-gray-500 mb-2">Minutes before shift end to mark as early departure</p>
                    <input
                      type="number"
                      value={settings.early_departure_threshold_minutes ?? 30}
                      onChange={(e) => setSettings({ ...settings, early_departure_threshold_minutes: parseInt(e.target.value) })}
                      className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Bonus Star Window (months)</label>
                    <p className="text-xs text-gray-500 mb-2">Bonus stars older than this rolling window are not counted</p>
                    <input
                      type="number"
                      min="1"
                      value={settings.bonus_star_window_months ?? 9}
                      onChange={(e) => setSettings({ ...settings, bonus_star_window_months: Math.max(parseInt(e.target.value || '1', 10), 1) })}
                      className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      id="auto_generate_performance"
                      checked={settings.auto_generate_performance_records ?? true}
                      onChange={(e) => setSettings({ ...settings, auto_generate_performance_records: e.target.checked })}
                      className="w-5 h-5 text-primary-600 rounded focus:ring-primary-500"
                    />
                    <div>
                      <label htmlFor="auto_generate_performance" className="text-sm font-medium text-gray-700">
                        Auto-Generate Performance Records
                      </label>
                      <p className="text-xs text-gray-500">
                        Automatically create performance violation records when staff are late/half-day
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'system' && (
            <div className="space-y-6 max-w-2xl">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-medium text-blue-800 mb-2">System Information</h4>
                <div className="text-sm text-blue-700 space-y-1">
                  <p>Version: 1.0.0</p>
                  <p>Database: SQLite</p>
                  <p>Backend: Django REST Framework</p>
                  <p>Frontend: React.js</p>
                </div>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <h4 className="font-medium text-yellow-800 mb-2">Data Backup</h4>
                <p className="text-sm text-yellow-700 mb-3">
                  Regular backups are recommended to prevent data loss. Downloads all data as a JSON file.
                </p>
                <button
                  onClick={handleBackup}
                  disabled={backingUp}
                  className="bg-yellow-600 hover:bg-yellow-700 text-white px-4 py-2 rounded-lg text-sm flex items-center gap-2 disabled:opacity-50"
                >
                  {backingUp ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  ) : (
                    <Download size={16} />
                  )}
                  {backingUp ? 'Downloading...' : 'Download Backup'}
                </button>
              </div>

              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <h4 className="font-medium text-red-800 mb-2">Danger Zone</h4>
                <p className="text-sm text-red-700 mb-3">
                  These actions are irreversible. This will delete all orders, expenses, wastage, inventory history, and reset stock to zero. Users, settings, menu, and categories will be preserved.
                </p>
                <button
                  onClick={() => setShowResetConfirm(true)}
                  className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg text-sm flex items-center gap-2"
                >
                  <AlertTriangle size={16} />
                  Reset All Data
                </button>
              </div>
            </div>
          )}

          {activeTab === 'users' && <Users embedded />}
          {activeTab === 'rbac' && <ACLManagement embedded />}
          {activeTab === 'media' && <MediaManagement embedded />}

          {/* Reset Confirmation Modal */}
          {showResetConfirm && (
            <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
              <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                    <AlertTriangle size={24} className="text-red-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-800">Reset All Data</h3>
                    <p className="text-sm text-gray-500">This action cannot be undone</p>
                  </div>
                </div>
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
                  <p className="text-sm text-red-700">
                    This will permanently delete all orders, payments, expenses, wastage records, inventory history, cash registers, and reset all stock quantities to zero.
                  </p>
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Type <strong className="text-red-600">RESET</strong> to confirm
                  </label>
                  <input
                    type="text"
                    value={resetConfirmText}
                    onChange={(e) => setResetConfirmText(e.target.value)}
                    placeholder="Type RESET here"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                  />
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={() => { setShowResetConfirm(false); setResetConfirmText(''); }}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleReset}
                    disabled={resetConfirmText !== 'RESET' || resetting}
                    className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {resetting ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    ) : (
                      <AlertTriangle size={16} />
                    )}
                    {resetting ? 'Resetting...' : 'Reset All Data'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Settings;
